import {
	CancelMeetingInput,
	type CreateMeetingInput,
	MeetingTargetInput,
	MeetingTargetType,
	RestoreMeetingInput,
	type UpdateMeetingInput,
	UpsertMeetingSeriesInput,
	useCancelMeetingMutation,
	useCreateMeetingMutation,
	useRestoreMeetingMutation,
	useUpdateMeetingMutation,
} from '@treams/treams-network';

export type RequiredSeriesMetadata<T extends MeetingTargetType> = T extends MeetingTargetType.Note
	? Required<Pick<UpsertMeetingSeriesInput, 'noteSeriesMetadata'>>
	: Required<Omit<UpsertMeetingSeriesInput, 'recurrenceFrequency'>>;

export default function useMeetingMutations(options: {
	target: MeetingTargetInput;
	hasMeeting?: boolean;
}) {
	const { target, hasMeeting } = options;
	const [create, { error: createError }] = useCreateMeetingMutation();
	const [update, { error: updateError }] = useUpdateMeetingMutation();
	const [cancel, { error: cancelError }] = useCancelMeetingMutation();
	const [restore, { error: restoreError }] = useRestoreMeetingMutation();

	const createMeeting = (input: Omit<CreateMeetingInput, 'target'>) => {
		try {
			return create({
				variables: { input: { target, ...input } },
			});
		} catch {
			return false;
		}
	};

	const updateMeeting = (input: Omit<UpdateMeetingInput, 'target'>) => {
		try {
			return update({
				variables: { input: { target, ...input } },
			});
		} catch {
			return false;
		}
	};

	const cancelMeeting = (input: Omit<CancelMeetingInput, 'target'>) => {
		try {
			return cancel({
				variables: { input: { target, ...input } },
			});
		} catch {
			return false;
		}
	};

	const restoreMeeting = (input?: Omit<RestoreMeetingInput, 'target'>) => {
		try {
			return restore({
				variables: { input: { target, ...input } },
			});
		} catch {
			return false;
		}
	};

	const upsertMeeting = (
		input: Omit<CreateMeetingInput, 'target'> & Omit<UpdateMeetingInput, 'target'>,
	) => (hasMeeting ? updateMeeting(input) : createMeeting(input));

	const error =
		createError?.graphQLErrors?.[0] ??
		updateError?.graphQLErrors?.[0] ??
		cancelError?.graphQLErrors?.[0] ??
		restoreError?.graphQLErrors?.[0];

	return {
		create: createMeeting,
		update: updateMeeting,
		upsert: upsertMeeting,
		cancel: cancelMeeting,
		restore: restoreMeeting,
		error,
	};
}
